import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import TechLogo from '../components/TechLogo';

const technologyStats = [
  { value: 'Architecture', label: 'pensee pour la croissance, pas juste pour le lancement' },
  { value: 'IA utile', label: 'integree quand elle cree du levier reel' },
  { value: 'Data claire', label: 'pilotage, tracking et CRM relies au business' }
];

const deliveryPrinciples = [
  {
    title: 'La bonne technologie au bon moment',
    description: 'Nous evitons les stacks inutiles. Le choix technique sert la marge, la vitesse, la maintenance et la lisibilite du projet.'
  },
  {
    title: 'Des fondations robustes',
    description: 'Performance, securite, scalabilite et maintenabilite sont pensees des le debut, pas ajoutees en correction plus tard.'
  },
  {
    title: 'Une execution lisible pour votre equipe',
    description: 'Documentation, automatisation, standards et structure claire pour que la solution reste un actif, pas une dependance opaque.'
  }
];

const architectureLayers = [
  {
    title: 'Experience',
    subtitle: 'Interfaces premium',
    description: 'Design system, parcours, motion utile et UX qui donne de la confiance a vos prospects comme a vos equipes.'
  },
  {
    title: 'Operations',
    subtitle: 'Automatisation et CRM',
    description: 'Lead flow, qualification, relances, paiements, reporting et synchronisation des outils au meme endroit.'
  },
  {
    title: 'Decision',
    subtitle: 'Data et IA',
    description: 'Dashboards, scoring, generation assistee, agents IA et automatisations qui reduisent le bruit operationnel.'
  },
  {
    title: 'Scale',
    subtitle: 'Cloud et fiabilite',
    description: 'Hebergement, monitoring, performance, securite et architecture capable de suivre une montee en gamme.'
  }
];

const techCategories = [
  {
    name: 'Frontend & Experience',
    icon: '01',
    summary: 'Des interfaces rapides, sobres et haut de gamme, pensees pour convaincre autant que pour convertir.',
    emphasis: 'Pour les marques qui vendent de la qualite et veulent que cela se sente des le premier ecran.',
    techs: [
      { name: 'React', logo: 'https://cdn.worldvectorlogo.com/logos/react-2.svg' },
      { name: 'Next.js', logo: 'https://cdn.worldvectorlogo.com/logos/nextjs-2.svg' },
      { name: 'Vue.js', logo: 'https://cdn.worldvectorlogo.com/logos/vue-9.svg' },
      { name: 'TypeScript', logo: 'https://cdn.worldvectorlogo.com/logos/typescript.svg' }
    ]
  },
  {
    name: 'Backend & Integrations',
    icon: '02',
    summary: 'Le moteur qui fait circuler les donnees, automatise les actions et relie proprement vos systemes.',
    emphasis: 'Pour les projets qui ont besoin de flux fiables, d integrations propres et de logique metier solide.',
    techs: [
      { name: 'Node.js', logo: 'https://cdn.worldvectorlogo.com/logos/nodejs-icon.svg' },
      { name: 'Python', logo: 'https://cdn.worldvectorlogo.com/logos/python-5.svg' },
      { name: 'Go', logo: 'https://cdn.worldvectorlogo.com/logos/go-6.svg' },
      { name: 'Java', logo: 'https://cdn.worldvectorlogo.com/logos/java-4.svg' }
    ]
  },
  {
    name: 'Data & Persistance',
    icon: '03',
    summary: 'Des bases propres, evolutives et pilotables pour que la data reste une force, pas une dette.',
    emphasis: 'Pour les equipes qui veulent des donnees actionnables, consolidees et raccordees au terrain.',
    techs: [
      { name: 'PostgreSQL', logo: 'https://cdn.worldvectorlogo.com/logos/postgresql.svg' },
      { name: 'MongoDB', logo: 'https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg' },
      { name: 'Redis', logo: 'https://cdn.worldvectorlogo.com/logos/redis.svg' },
      { name: 'MySQL', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/Mysql.svg' }
    ]
  },
  {
    name: 'Cloud & Performance',
    icon: '04',
    summary: 'Une infrastructure preparee pour la vitesse, les pics de trafic, la securite et la serenite operationnelle.',
    emphasis: 'Pour les entreprises qui veulent grandir sans rebatir leur socle tous les six mois.',
    techs: [
      { name: 'AWS', logo: 'https://cdn.worldvectorlogo.com/logos/aws-2.svg' },
      { name: 'Google Cloud', logo: 'https://cdn.worldvectorlogo.com/logos/google-cloud-1.svg' },
      { name: 'Azure', logo: 'https://cdn.worldvectorlogo.com/logos/azure-1.svg' },
      { name: 'Vercel', logo: 'https://cdn.worldvectorlogo.com/logos/vercel.svg' }
    ]
  },
  {
    name: 'Delivery & DevOps',
    icon: '05',
    summary: 'Tests, deploiements, observabilite et cadence d execution pour sortir plus vite avec moins de friction.',
    emphasis: 'Pour fiabiliser la production, accelerer les iterations et garder une qualite constante.',
    techs: [
      { name: 'Docker', logo: 'https://cdn.worldvectorlogo.com/logos/docker.svg' },
      { name: 'Kubernetes', logo: 'https://upload.wikimedia.org/wikipedia/commons/3/39/Kubernetes_logo_without_workmark.svg' },
      { name: 'Jenkins', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e9/Jenkins_logo.svg' },
      { name: 'GitLab', logo: 'https://cdn.worldvectorlogo.com/logos/gitlab.svg' }
    ]
  },
  {
    name: 'IA & Automatisation',
    icon: '06',
    summary: 'Des briques IA et des flux intelligents integres avec exigence, la ou elles apportent un vrai avantage.',
    emphasis: 'Pour automatiser sans degrader l experience, et augmenter vos equipes sans ajouter de complexite.',
    techs: [
      { name: 'OpenAI', logo: 'https://cdn.worldvectorlogo.com/logos/openai-2.svg' },
      { name: 'Hugging Face', logo: 'https://huggingface.co/front/assets/huggingface_logo.svg' },
      { name: 'TensorFlow', logo: 'https://cdn.worldvectorlogo.com/logos/tensorflow-2.svg' },
      { name: 'PyTorch', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/10/PyTorch_logo_icon.svg' }
    ]
  }
];

const Technologies = () => {
  return (
    <>
      <Helmet>
        <title>Nos Technologies - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Notre stack technologique premium: experience, architecture, cloud, data, IA et automatisation au service de la croissance."
        />
      </Helmet>

      <main>
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,106,0,0.2),_transparent_40%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_28%)]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 backdrop-blur"
              >
                <span className="h-2 w-2 rounded-full bg-neon-orange" />
                La technologie doit servir le business
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.08 }}
                className="mx-auto mt-6 max-w-5xl text-4xl font-bold leading-tight text-white md:text-6xl"
              >
                Une stack choisie pour
                <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> durer, convertir et scaler</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.16 }}
                className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-300 md:text-xl"
              >
                Nous ne collectionnons pas les logos. Nous assemblons des technologies fiables, pertinentes et
                maintenables pour soutenir une execution premium, de l experience client jusqu aux operations.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.24 }}
              className="mt-12 grid gap-4 md:grid-cols-3"
            >
              {technologyStats.map((item) => (
                <div key={item.value} className="rounded-[26px] border border-white/10 bg-white/5 p-6 text-left backdrop-blur">
                  <div className="text-lg font-semibold text-white">{item.value}</div>
                  <p className="mt-3 text-sm leading-6 text-gray-300">{item.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-gradient-to-b from-[#f8f6f2] via-white to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Notre doctrine</p>
                <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Plus d exigence, moins de bruit technique</h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-gray-600">
                  Un projet premium ne se juge pas au nombre d outils affiches. Il se juge a la nettete de
                  l experience, a la fluidite des operations, a la facilite de maintenance et a la valeur qu il
                  cree sur la duree.
                </p>
              </motion.div>

              <div className="grid gap-4 md:grid-cols-3">
                {deliveryPrinciples.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="rounded-[26px] border border-gray-200 bg-white p-6 shadow-[0_18px_60px_rgba(17,24,39,0.06)]"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-600">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {architectureLayers.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-[26px] bg-gray-950 p-6 text-white shadow-xl shadow-black/10"
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-neon-orange">{item.subtitle}</p>
                  <h3 className="mt-3 text-2xl font-semibold">{item.title}</h3>
                  <p className="mt-4 text-sm leading-6 text-gray-300">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-white to-[#f8f6f2] relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Stack & execution</p>
              <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Nos briques de travail</h2>
              <p className="mt-5 text-base leading-8 text-gray-600">
                Chaque couche a un role clair dans la performance du projet. Nous construisons des systemes qui
                donnent une image forte, une execution propre et une lecture business plus precise.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {techCategories.map((category, index) => (
                <motion.div
                  key={category.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-[30px] border border-gray-200 bg-white p-7 shadow-[0_22px_70px_rgba(17,24,39,0.06)]"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,106,0,0.08),_transparent_35%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neon-orange/10 text-sm font-semibold text-neon-orange">
                          {category.icon}
                        </div>
                        <h3 className="mt-4 text-2xl font-bold text-gray-900">{category.name}</h3>
                      </div>
                      <div className="rounded-full border border-gray-200 bg-[#f8f6f2] px-3 py-1 text-xs uppercase tracking-[0.16em] text-gray-500">
                        Premium stack
                      </div>
                    </div>

                    <p className="mt-5 text-base leading-7 text-gray-700">{category.summary}</p>
                    <p className="mt-4 text-sm leading-6 text-gray-500">{category.emphasis}</p>

                    <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4">
                      {category.techs.map((tech) => (
                        <div
                          key={tech.name}
                          className="rounded-2xl border border-gray-100 bg-[#fbfaf8] p-4 text-center transition-all duration-300 group-hover:border-neon-orange/20"
                        >
                          <div className="mx-auto flex h-12 w-12 items-center justify-center">
                            <TechLogo
                              name={tech.name}
                              src={tech.logo}
                              className="h-full w-full text-xl"
                              imgClassName="h-full w-full object-contain"
                            />
                          </div>
                          <div className="mt-3 text-sm font-medium text-gray-700">{tech.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,106,0,0.16),_transparent_40%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-white md:text-5xl"
            >
              Besoin d une architecture plus
              <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> ambitieuse et plus claire</span> ?
            </motion.h2>

            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-300">
              Nous pouvons auditer l existant, proposer une cible plus premium et construire une execution plus
              propre, plus rapide et plus rentable.
            </p>

            <Link
              to="/contact"
              className="mt-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-neon-orange/20"
            >
              Parler a un expert
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Technologies;
